import React from 'react';
import { Server, ServerOff, WifiOff, Wifi, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';
import { useServerHealth } from '../hooks/useServerHealth';
import { useOnlineStatus } from '../hooks/useOnlineStatus';

interface ServerStatusIndicatorProps {
  showLabel?: boolean;
  className?: string;
}

/**
 * Component to display server connection status
 * Shows different states: Online, Server Off, Network Off, Checking
 */
const ServerStatusIndicator: React.FC<ServerStatusIndicatorProps> = ({ 
  showLabel = true,
  className = '' 
}) => {
  const { isOnline: networkOnline } = useOnlineStatus();
  const { isServerOnline, isChecking, error, recheckNow } = useServerHealth(30000); // Check every 30s

  // Determine status
  const getStatus = () => {
    if (!networkOnline) {
      return {
        icon: WifiOff,
        color: 'bg-red-500',
        textColor: 'text-red-600',
        label: 'Tidak Ada Jaringan',
        description: 'Koneksi internet terputus',
        severity: 'critical',
      };
    }

    if (isChecking) {
      return {
        icon: RefreshCw,
        color: 'bg-gray-400',
        textColor: 'text-gray-600',
        label: 'Memeriksa...',
        description: 'Memeriksa koneksi server',
        severity: 'info',
      };
    }

    if (!isServerOnline) {
      return {
        icon: ServerOff,
        color: 'bg-orange-500',
        textColor: 'text-orange-600',
        label: 'Server Offline',
        description: error || 'Server tidak dapat diakses',
        severity: 'warning',
      };
    }

    return {
      icon: CheckCircle,
      color: 'bg-green-500',
      textColor: 'text-green-600',
      label: 'Server Online',
      description: 'Koneksi ke server normal',
      severity: 'success',
    };
  };

  const status = getStatus();
  const Icon = status.icon;

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Status Badge */}
      <div 
        className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${status.color} text-white text-sm font-medium cursor-pointer hover:opacity-90 transition-opacity`}
        onClick={recheckNow}
        title={`${status.description}\nKlik untuk memeriksa ulang`}
      >
        <Icon 
          size={16} 
          className={isChecking ? 'animate-spin' : ''} 
        />
        {showLabel && <span>{status.label}</span>}
      </div>

      {/* Warning Icon for Critical States */}
      {(status.severity === 'critical' || status.severity === 'warning') && (
        <div className="relative group">
          <AlertCircle size={20} className={`${status.textColor} animate-pulse`} />
          
          {/* Tooltip on hover */}
          <div className="absolute right-0 top-full mt-2 w-64 p-3 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
            <div className="flex items-start gap-2">
              <AlertCircle size={16} className={status.textColor} />
              <div className="text-xs">
                <p className="font-semibold text-gray-900 mb-1">{status.label}</p>
                <p className="text-gray-600">{status.description}</p>
                {!networkOnline && (
                  <p className="mt-2 text-yellow-700">
                    💡 Mode Offline: Transaksi akan disimpan dan di-sync saat online
                  </p>
                )}
                {networkOnline && !isServerOnline && (
                  <p className="mt-2 text-orange-700">
                    💡 Mode Offline: Transaksi akan disimpan dan di-sync saat server kembali
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServerStatusIndicator;
